import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AddPoolDefinitionToProgramDefinitionOptions,
  AddPoolDefinitionToProgramDefinitionResponse,
  AddPoolDefinitionToProgramDefinitionValidationErrors,
  BulkCreatePoolDefinitionsOptions,
  BulkCreatePoolDefinitionsResponse,
  BulkDeletePoolDefinitionsOptions,
  BulkDeletePoolDefinitionsResponse,
  BulkUpdatePoolDefinitionsOptions,
  BulkUpdatePoolDefinitionsResponse,
  BulkUpdatePoolDefinitionsValidationErrors,
  CascadeWithLiterals,
  CreatePoolDefinitionOptions,
  FindPoolDefinitionsByProgramDefinitionOptions,
  FindPoolDefinitionsByProgramDefinitionResponse,
  PoolDefinition,
  PoolDefinitionQuery,
  PoolDefinitionsQueryBuilder,
  QueryPoolDefinitionsOptions,
  QueryPoolDefinitionsResponse,
  RemovePoolDefinitionFromProgramDefinitionOptions,
  RemovePoolDefinitionFromProgramDefinitionResponse,
  RemovePoolDefinitionFromProgramDefinitionValidationErrors,
  UpdatePoolDefinition,
  UpdatePoolDefinitionOptions,
  UpdatePoolDefinitionValidationErrors,
  addPoolDefinitionToProgramDefinition as universalAddPoolDefinitionToProgramDefinition,
  bulkCreatePoolDefinitions as universalBulkCreatePoolDefinitions,
  bulkDeletePoolDefinitions as universalBulkDeletePoolDefinitions,
  bulkUpdatePoolDefinitions as universalBulkUpdatePoolDefinitions,
  createPoolDefinition as universalCreatePoolDefinition,
  deletePoolDefinition as universalDeletePoolDefinition,
  findPoolDefinitionsByProgramDefinition as universalFindPoolDefinitionsByProgramDefinition,
  getPoolDefinition as universalGetPoolDefinition,
  queryPoolDefinitions as universalQueryPoolDefinitions,
  removePoolDefinitionFromProgramDefinition as universalRemovePoolDefinitionFromProgramDefinition,
  typedQueryPoolDefinitions as universalTypedQueryPoolDefinitions,
  updatePoolDefinition as universalUpdatePoolDefinition,
} from './benefit-programs-v1-pool-definition-pool-definitions.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/benefit-programs' };

export function createPoolDefinition(
  httpClient: HttpClient
): CreatePoolDefinitionSignature {
  return (
    poolDefinition: NonNullablePaths<
      PoolDefinition,
      | `details`
      | `details.benefits`
      | `details.benefits.${number}.benefitKey`
      | `details.benefits.${number}.providerAppId`
      | `namespace`,
      5
    >,
    options?: CreatePoolDefinitionOptions
  ) =>
    universalCreatePoolDefinition(
      poolDefinition,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreatePoolDefinitionSignature {
  /**
   * Creates a pool definition.
   * @param - Pool definition to create.
   * @returns Created pool definition.
   */
  (
    poolDefinition: NonNullablePaths<
      PoolDefinition,
      | `details`
      | `details.benefits`
      | `details.benefits.${number}.benefitKey`
      | `details.benefits.${number}.providerAppId`
      | `namespace`,
      5
    >,
    options?: CreatePoolDefinitionOptions
  ): Promise<
    NonNullablePaths<
      PoolDefinition,
      | `displayName`
      | `programDefinitionIds`
      | `details.benefits`
      | `details.benefits.${number}.benefitKey`
      | `details.creditConfiguration.amount`
      | `programDefinitions`
      | `programDefinitions.${number}._id`,
      5
    >
  >;
}

export function bulkCreatePoolDefinitions(
  httpClient: HttpClient
): BulkCreatePoolDefinitionsSignature {
  return (
    poolDefinitions: NonNullablePaths<
      PoolDefinition,
      | `details`
      | `details.benefits`
      | `details.benefits.${number}.benefitKey`
      | `details.benefits.${number}.providerAppId`
      | `namespace`,
      5
    >[],
    options?: BulkCreatePoolDefinitionsOptions
  ) =>
    universalBulkCreatePoolDefinitions(
      poolDefinitions,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreatePoolDefinitionsSignature {
  /**
   * Creates pool definitions.
   * @param - Pool definitions to create.
   */
  (
    poolDefinitions: NonNullablePaths<
      PoolDefinition,
      | `details`
      | `details.benefits`
      | `details.benefits.${number}.benefitKey`
      | `details.benefits.${number}.providerAppId`
      | `namespace`,
      5
    >[],
    options?: BulkCreatePoolDefinitionsOptions
  ): Promise<
    NonNullablePaths<
      BulkCreatePoolDefinitionsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.displayName`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function updatePoolDefinition(
  httpClient: HttpClient
): UpdatePoolDefinitionSignature {
  return (
    _id: string,
    poolDefinition: NonNullablePaths<UpdatePoolDefinition, `revision`, 2>,
    options?: UpdatePoolDefinitionOptions
  ) =>
    universalUpdatePoolDefinition(
      _id,
      poolDefinition,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdatePoolDefinitionSignature {
  /**
   * Updates a pool definition.
   *
   * Each time the pool definition is updated,
   * `revision` increments by 1.
   * The current `revision` must be passed when updating the pool definition.
   * This ensures you're working with the latest pool definition
   * and prevents unintended overwrites.
   * @param - Pool definition ID.
   * @returns Updated pool definition.
   */
  (
    _id: string,
    poolDefinition: NonNullablePaths<UpdatePoolDefinition, `revision`, 2>,
    options?: UpdatePoolDefinitionOptions
  ): Promise<
    NonNullablePaths<
      PoolDefinition,
      | `displayName`
      | `programDefinitionIds`
      | `details.benefits`
      | `details.benefits.${number}.benefitKey`
      | `details.creditConfiguration.amount`
      | `programDefinitions`
      | `programDefinitions.${number}._id`,
      5
    > & {
      __validationErrorsType?: UpdatePoolDefinitionValidationErrors;
    }
  >;
}

export function bulkUpdatePoolDefinitions(
  httpClient: HttpClient
): BulkUpdatePoolDefinitionsSignature {
  return (
    options?: NonNullablePaths<
      BulkUpdatePoolDefinitionsOptions,
      | `poolDefinitions.${number}.poolDefinition`
      | `poolDefinitions.${number}.poolDefinition._id`
      | `poolDefinitions.${number}.poolDefinition.revision`,
      5
    >
  ) =>
    universalBulkUpdatePoolDefinitions(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdatePoolDefinitionsSignature {
  /**
   * Updates pool definitions.
   */
  (
    options?: NonNullablePaths<
      BulkUpdatePoolDefinitionsOptions,
      | `poolDefinitions.${number}.poolDefinition`
      | `poolDefinitions.${number}.poolDefinition._id`
      | `poolDefinitions.${number}.poolDefinition.revision`,
      5
    >
  ): Promise<
    NonNullablePaths<
      BulkUpdatePoolDefinitionsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.displayName`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __validationErrorsType?: BulkUpdatePoolDefinitionsValidationErrors;
    }
  >;
}

export function deletePoolDefinition(
  httpClient: HttpClient
): DeletePoolDefinitionSignature {
  return (poolDefinitionId: string, cascade: CascadeWithLiterals) =>
    universalDeletePoolDefinition(
      poolDefinitionId,
      cascade,
      // @ts-ignore
      { httpClient }
    );
}

interface DeletePoolDefinitionSignature {
  /**
   * Deletes a pool definition.
   * @param - ID of the pool definition to delete.
   * @param - *Required**. Determines when the changes to this pool definition the program definitions containing it will be applied to associated programs and pools.
   */
  (poolDefinitionId: string, cascade: CascadeWithLiterals): Promise<void>;
}

export function bulkDeletePoolDefinitions(
  httpClient: HttpClient
): BulkDeletePoolDefinitionsSignature {
  return (
    poolDefinitionIds: string[],
    options?: BulkDeletePoolDefinitionsOptions
  ) =>
    universalBulkDeletePoolDefinitions(
      poolDefinitionIds,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeletePoolDefinitionsSignature {
  /**
   * Deletes pool definitions.
   * @param - List of IDs of the pool definitions to delete.
   */
  (
    poolDefinitionIds: string[],
    options?: BulkDeletePoolDefinitionsOptions
  ): Promise<
    NonNullablePaths<
      BulkDeletePoolDefinitionsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.displayName`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function getPoolDefinition(
  httpClient: HttpClient
): GetPoolDefinitionSignature {
  return (poolDefinitionId: string) =>
    universalGetPoolDefinition(
      poolDefinitionId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetPoolDefinitionSignature {
  /**
   * Retrieves a pool definition.
   * @param - ID of the pool definition to retrieve.
   * @returns Retrieved pool definition.
   */
  (poolDefinitionId: string): Promise<
    NonNullablePaths<
      PoolDefinition,
      | `displayName`
      | `programDefinitionIds`
      | `details.benefits`
      | `details.benefits.${number}.benefitKey`
      | `details.creditConfiguration.amount`
      | `programDefinitions`
      | `programDefinitions.${number}._id`,
      5
    >
  >;
}

export function queryPoolDefinitions(
  httpClient: HttpClient
): QueryPoolDefinitionsSignature {
  return (options?: QueryPoolDefinitionsOptions) =>
    universalQueryPoolDefinitions(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryPoolDefinitionsSignature {
  /**
   * Creates a query to retrieve a list of pool definitions.
   *
   * The Query Pool Definitions method builds a query to retrieve a list of pool definitions and returns a `PoolDefinitionsQueryBuilder` object.
   *
   * The returned object contains the query definition, which is used to run the query using the `find()` method.
   *
   * You can refine the query by chaining `PoolDefinitionsQueryBuilder` methods onto the query. `PoolDefinitionsQueryBuilder` methods enable you to filter, sort, and control the results that Query Pool Definitions returns.
   *
   * Query Pool Definitions has a default paging limit of 50, which you can override.
   *
   *  For a full description of the item object, see the object returned for the `items` property in `PoolDefinitionsQueryResult`.
   */
  (options?: QueryPoolDefinitionsOptions): PoolDefinitionsQueryBuilder;
}

export function typedQueryPoolDefinitions(
  httpClient: HttpClient
): TypedQueryPoolDefinitionsSignature {
  return (query: PoolDefinitionQuery, options?: QueryPoolDefinitionsOptions) =>
    universalTypedQueryPoolDefinitions(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryPoolDefinitionsSignature {
  /** */
  (query: PoolDefinitionQuery, options?: QueryPoolDefinitionsOptions): Promise<
    NonNullablePaths<
      QueryPoolDefinitionsResponse,
      | `poolDefinitions`
      | `poolDefinitions.${number}.displayName`
      | `poolDefinitions.${number}.details.creditConfiguration.amount`,
      6
    >
  >;
}

export function addPoolDefinitionToProgramDefinition(
  httpClient: HttpClient
): AddPoolDefinitionToProgramDefinitionSignature {
  return (
    poolDefinitionId: string,
    options: NonNullablePaths<
      AddPoolDefinitionToProgramDefinitionOptions,
      `cascade` | `programDefinitionId`,
      2
    >
  ) =>
    universalAddPoolDefinitionToProgramDefinition(
      poolDefinitionId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface AddPoolDefinitionToProgramDefinitionSignature {
  /**
   * Adds a pool definition to a program definition.
   *
   * Pool definitions can be contained by multiple program definitions, so this doesn't affect other program definitions that contain this pool definition.
   * @param - ID of the pool definition to add to the program definition.
   */
  (
    poolDefinitionId: string,
    options: NonNullablePaths<
      AddPoolDefinitionToProgramDefinitionOptions,
      `cascade` | `programDefinitionId`,
      2
    >
  ): Promise<
    NonNullablePaths<
      AddPoolDefinitionToProgramDefinitionResponse,
      | `poolDefinition.displayName`
      | `poolDefinition.programDefinitionIds`
      | `poolDefinition.details.benefits`
      | `poolDefinition.details.benefits.${number}.benefitKey`
      | `poolDefinition.details.creditConfiguration.amount`
      | `poolDefinition.programDefinitions`
      | `poolDefinition.programDefinitions.${number}._id`,
      6
    > & {
      __validationErrorsType?: AddPoolDefinitionToProgramDefinitionValidationErrors;
    }
  >;
}

export function removePoolDefinitionFromProgramDefinition(
  httpClient: HttpClient
): RemovePoolDefinitionFromProgramDefinitionSignature {
  return (
    poolDefinitionId: string,
    options: NonNullablePaths<
      RemovePoolDefinitionFromProgramDefinitionOptions,
      `cascade` | `programDefinitionId`,
      2
    >
  ) =>
    universalRemovePoolDefinitionFromProgramDefinition(
      poolDefinitionId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface RemovePoolDefinitionFromProgramDefinitionSignature {
  /**
   * Removes a pool definition from a program definition.
   *
   * Pool definitions don't need to be contained by a program definition, so this doesn't affect the usability of this pool definition.
   * @param - ID of the pool definition to remove from the program definition.
   */
  (
    poolDefinitionId: string,
    options: NonNullablePaths<
      RemovePoolDefinitionFromProgramDefinitionOptions,
      `cascade` | `programDefinitionId`,
      2
    >
  ): Promise<
    NonNullablePaths<
      RemovePoolDefinitionFromProgramDefinitionResponse,
      | `poolDefinition.displayName`
      | `poolDefinition.programDefinitionIds`
      | `poolDefinition.details.benefits`
      | `poolDefinition.details.benefits.${number}.benefitKey`
      | `poolDefinition.details.creditConfiguration.amount`
      | `poolDefinition.programDefinitions`
      | `poolDefinition.programDefinitions.${number}._id`,
      6
    > & {
      __validationErrorsType?: RemovePoolDefinitionFromProgramDefinitionValidationErrors;
    }
  >;
}

export function findPoolDefinitionsByProgramDefinition(
  httpClient: HttpClient
): FindPoolDefinitionsByProgramDefinitionSignature {
  return (
    programDefinitionId: string,
    options: NonNullablePaths<
      FindPoolDefinitionsByProgramDefinitionOptions,
      `namespace`,
      2
    >
  ) =>
    universalFindPoolDefinitionsByProgramDefinition(
      programDefinitionId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface FindPoolDefinitionsByProgramDefinitionSignature {
  /**
   * Retrieve all a program definition's pool definitions.
   * @param - ID of the program definition to retrieve its pool definitions.
   */
  (
    programDefinitionId: string,
    options: NonNullablePaths<
      FindPoolDefinitionsByProgramDefinitionOptions,
      `namespace`,
      2
    >
  ): Promise<
    NonNullablePaths<
      FindPoolDefinitionsByProgramDefinitionResponse,
      | `poolDefinitions`
      | `poolDefinitions.${number}.displayName`
      | `poolDefinitions.${number}.details.creditConfiguration.amount`,
      6
    >
  >;
}

export {
  AccountInfo,
  ActionEvent,
  AddPoolDefinitionToProgramDefinitionOptions,
  AddPoolDefinitionToProgramDefinitionRequest,
  AddPoolDefinitionToProgramDefinitionResponse,
  ApplicationError,
  Benefit,
  BulkActionMetadata,
  BulkCreatePoolDefinitionsOptions,
  BulkCreatePoolDefinitionsRequest,
  BulkCreatePoolDefinitionsResponse,
  BulkDeletePoolDefinitionsOptions,
  BulkDeletePoolDefinitionsRequest,
  BulkDeletePoolDefinitionsResponse,
  BulkPoolDefinitionResult,
  BulkUpdatePoolDefinitionsOptions,
  BulkUpdatePoolDefinitionsRequest,
  BulkUpdatePoolDefinitionsResponse,
  Cascade,
  CreatePoolDefinitionOptions,
  CreatePoolDefinitionRequest,
  CreatePoolDefinitionResponse,
  CreditConfiguration,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  CustomPolicy,
  DeletePoolDefinitionRequest,
  DeletePoolDefinitionResponse,
  Details,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  ExtendedFields,
  FindPoolDefinitionsByProgramDefinitionOptions,
  FindPoolDefinitionsByProgramDefinitionRequest,
  FindPoolDefinitionsByProgramDefinitionResponse,
  FixedIntervalPolicy,
  GetPoolDefinitionRequest,
  GetPoolDefinitionResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ItemMetadata,
  MaskedPoolDefinition,
  MessageEnvelope,
  Policy,
  PolicyExpression,
  PolicyExpressionAnd,
  PolicyExpressionExpressionOneOf,
  PolicyExpressionNot,
  PolicyExpressionOr,
  PolicyExpressionType,
  PolicyPolicyOneOf,
  PoolDefinition,
  PoolDefinitionAddedToProgramDefinition,
  PoolDefinitionDeletedProxySupport,
  PoolDefinitionQuerySpec,
  PoolDefinitionRemovedFromProgramDefinition,
  PoolDefinitionTranslated,
  PoolDefinitionTranslationDeleted,
  PoolDefinitionUpdatedProxySupport,
  PoolDefinitionsQueryBuilder,
  PoolDefinitionsQueryResult,
  ProgramDefinitionInfo,
  QueryPoolDefinitionsOptions,
  QueryPoolDefinitionsRequest,
  QueryPoolDefinitionsResponse,
  RateLimitedPolicy,
  RemovePoolDefinitionFromProgramDefinitionOptions,
  RemovePoolDefinitionFromProgramDefinitionRequest,
  RemovePoolDefinitionFromProgramDefinitionResponse,
  RestoreInfo,
  RolloverConfiguration,
  SortOrder,
  Sorting,
  TimePeriod,
  Type,
  UpdatePoolDefinition,
  UpdatePoolDefinitionOptions,
  UpdatePoolDefinitionRequest,
  UpdatePoolDefinitionResponse,
  WebhookIdentityType,
  WeekDay,
  utils,
} from './benefit-programs-v1-pool-definition-pool-definitions.universal.js';
